import { DEALER_STATUS } from 'constants/StatusConstants';
import * as actionTypes from 'modules/dealers/actionTypes';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

/**
 * @typedef {{
 *      isLoading: boolean,
 *      apiError: string,
 *      ids: Array<string>,
 *      page: import('modules/global/generic.types').Page,
 *      filters: {
 *          text?: string,
 *          features ?: Array<import('modules/global/typing/enums/features.enum').FeaturesEnum>,
 *          extensions?: Array<import('modules/global/typing/enums/extensions.enum').ExtensionEnum>,
 *          groupIds?: Array<string>,
 *          status?: Array<import("modules/global/typing/enums/dealers.enum").DealerStatusEnum>,
 *      }
 * }} SliceSelect
 *
 * @typedef {{
 *     [token:string] : SliceSelect,
 * }} State
 */

/** @type {State} */
const initialState = {};

/** @type {SliceSelect} */
export const emptySlice = {
  isLoading: false,
  apiError: null,
  ids: [],
  page: { number: 0, totalElements: 0, totalPages: 0, size: 10 },
  filters: {
    status: [DEALER_STATUS.ACTIVE],
  },
};

/**
 * @param {State} state
 * @param {import('modules/dealers/types').setPaginatedDealersFilters} action
 * @returns {State}
 */
const setFilters = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...(state[payload.token] ?? emptySlice),
    filters: {
      ...(state[payload.token]?.filters ?? emptySlice.filters),
      ...payload.filters,
    },
  },
});

/**
 * @param {State} state
 * @param {import('modules/dealers/types').GetPaginatedDealersAction} action
 * @returns {State}
 */
const beforeFetchDealers = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    isLoading: true,
    apiError: null,
  },
});

/**
 * @param {State} state
 * @param {import('modules/dealers/types').GetPaginatedDealersFailureAction} action
 * @returns {State}
 */
const hasError = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    isLoading: false,
    apiError: payload.message,
  },
});

/**
 * @param {State} state
 * @param {import('modules/dealers/types').GetPaginatedDealersSuccessAction} action
 * @returns {State}
 */
const updateDealerSelect = (state, { payload }) => ({
  ...state,
  [payload.token]: {
    ...state[payload.token],
    isLoading: false,
    apiError: null,
    page: payload.page,
    ids: uniq(
      payload.content.reduce(
        (acc, dealer) => [...acc, dealer.id],
        payload.page.number === 0 ? [] : state[payload.token].ids
      )
    ),
  },
});

export default createReducer(initialState, {
  [actionTypes.SET_PAGINATED_DEALERS_FILTERS]: setFilters,

  [actionTypes.GET_PAGINATED_DEALERS_FETCH]: beforeFetchDealers,
  [actionTypes.GET_PAGINATED_DEALERS_FETCH_FAILURE]: hasError,
  [actionTypes.GET_PAGINATED_DEALERS_FETCH_SUCCESS]: updateDealerSelect,
});
